console.log('VITE_APP_BASE_URL:', import.meta.env.VITE_APP_BASE_URL);
import "../sentry.js";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { GoogleOAuthProvider } from "@react-oauth/google";

import ErrorBoundary from "./exceptionHandlers/ErrorBoundry.tsx";

import App from "./App.tsx";

import "./index.css";
import "./App.css";
import "react-calendar/dist/Calendar.css";
import "./styles/phone-input.css";

// Global error handlers for chunk loading and other unhandled errors
const handleGlobalError = (error: ErrorEvent) => {
  const isChunkLoadError =
    error.message?.includes("Loading chunk") ||
    error.message?.includes("Failed to fetch dynamically imported module") ||
    error.message?.includes("dynamically imported module");

  // Suppress Vite HMR WebSocket token errors (harmless development-only errors)
  const isViteHMRError =
    error.message?.includes("__WS_TOKEN__") ||
    error.message?.includes("is not defined") && error.filename?.includes("client");

  if (isChunkLoadError) {
    console.log("Chunk loading error detected (auto-reload temporarily disabled)");
    // window.location.reload(); // Temporarily disabled to debug
    return;
  }

  if (isViteHMRError) {
    // Silently ignore Vite HMR WebSocket token errors - these are harmless
    // The HMR will work once the token is properly injected
    return;
  }

  // Let other errors be handled by Sentry/ErrorBoundary
  console.error("Global error:", error);
};

const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
  const error = event.reason;
  const isChunkLoadError =
    error?.message?.includes("Loading chunk") ||
    error?.message?.includes("Failed to fetch dynamically imported module") ||
    error?.message?.includes("dynamically imported module") ||
    error?.toString?.().includes("Loading CSS chunk");

  // Suppress Vite HMR WebSocket token errors (harmless development-only errors)
  const isViteHMRError =
    error?.message?.includes("__WS_TOKEN__") ||
    (error?.message?.includes("is not defined") && error?.stack?.includes("client"));

  if (isChunkLoadError) {
    console.log("Chunk loading promise rejection detected (auto-reload temporarily disabled)");
    // window.location.reload(); // Temporarily disabled to debug
    event.preventDefault(); // Prevent the error from being logged
    return;
  }

  if (isViteHMRError) {
    // Silently ignore Vite HMR WebSocket token errors - these are harmless
    event.preventDefault();
    return;
  }

  // Let other promise rejections be handled normally
  console.error("Unhandled promise rejection:", error);
};

// Add global error handlers
window.addEventListener("error", handleGlobalError);
window.addEventListener("unhandledrejection", handleUnhandledRejection);

const queryClient = new QueryClient();

// Debug: Check if root element exists
const rootElement = document.getElementById("root");
const googleClientId = import.meta.env.VITE_GOOGLE_OAUTH2_CLIENT_ID;

if (!rootElement) {
  console.error("Root element not found!");
  document.body.innerHTML = '<div style="padding: 20px; color: red;">Error: Root element not found!</div>';
} else {
  console.log("Root element found, mounting React...");
  console.log("Google Client ID:", googleClientId ? "Set" : "Not set");
  
  const appContent = (
    <StrictMode>
      {googleClientId ? (
        <GoogleOAuthProvider clientId={googleClientId}>
          <QueryClientProvider client={queryClient}>
            <ErrorBoundary>
              <App />
            </ErrorBoundary>
          </QueryClientProvider>
        </GoogleOAuthProvider>
      ) : (
        <QueryClientProvider client={queryClient}>
          <ErrorBoundary>
            <App />
          </ErrorBoundary>
        </QueryClientProvider>
      )}
    </StrictMode>
  );
  
  createRoot(rootElement).render(appContent);
  console.log("React mounted successfully");
}
